<template>
  <div
    class="absolute idle-chrome-root text-white"
    :class="viewportRotationClasses"
  >
    <div
      v-if="hibernate"
      class="absolute inset-0 z-10 bg-black"
      aria-hidden="true"
    />

    <template v-else>
      <div
        class="relative w-full h-full overflow-hidden"
        :class="useDefaultChrome ? 'background' : 'bg-black'"
      >
        <div
          v-if="backgroundLayerStyle"
          class="absolute inset-0 pointer-events-none"
          :style="backgroundLayerStyle"
          aria-hidden="true"
        />
        <template v-if="useDefaultChrome">
          <div class="absolute -top-1/4 -left-1/4 w-3/4 h-3/4 rounded-full bg-[#FF7A1A]/10 blur-[8vw] pointer-events-none" />
          <div class="absolute -bottom-1/4 -right-1/4 w-2/3 h-2/3 rounded-full bg-[#C2410C]/8 blur-[8vw] pointer-events-none" />
        </template>
        <div
          v-else
          class="absolute inset-0 bg-black/35 pointer-events-none"
          aria-hidden="true"
        />

        <!-- Screensaver motion: logo + slot bounce together (long idle burn-in protection). -->
        <div
          v-if="screensaverMotion && !compactLayout"
          ref="screensaverBoundsRef"
          class="idle-screensaver-bounds absolute inset-0 overflow-hidden px-[6vmin] motion-reduce:flex motion-reduce:items-center motion-reduce:justify-center"
        >
          <div
            ref="screensaverContentRef"
            class="idle-screensaver-content absolute left-0 top-0 flex max-w-full flex-col items-center text-center gap-[3vh] will-change-transform motion-reduce:static"
          >
            <div
              class="player-idle-chrome-logo-wrap"
              :class="[
                compactLayout ? 'player-idle-chrome-logo-wrap--compact' : undefined,
                logoWrapClass,
                useDefaultChrome
                  ? '[filter:drop-shadow(0_0_48px_rgba(200,137,63,0.35))_drop-shadow(0_0_96px_rgba(200,137,63,0.12))]'
                  : 'drop-shadow-[0_4px_24px_rgba(0,0,0,0.55)]',
              ]"
              :style="logoWrapStyle"
            >
              <img
                v-if="logoUrl"
                :src="logoUrl"
                :alt="logoAlt"
                class="player-idle-chrome-logo"
              >
            </div>
            <div class="shrink-0 w-full min-w-0 max-w-full">
              <slot />
            </div>
          </div>
        </div>
        <div
          v-else
          class="absolute inset-0 flex flex-col items-center px-[6vmin] transition-all duration-[950ms] motion-reduce:duration-75 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
          :class="compactLayout ? 'pt-[4vh] justify-start' : 'justify-center'"
        >
          <div
            class="player-idle-chrome-logo-wrap transition-all duration-[950ms] motion-reduce:duration-75 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
            :class="[
              compactLayout ? 'player-idle-chrome-logo-wrap--compact' : undefined,
              logoWrapClass,
              useDefaultChrome
                ? '[filter:drop-shadow(0_0_48px_rgba(200,137,63,0.35))_drop-shadow(0_0_96px_rgba(200,137,63,0.12))]'
                : 'drop-shadow-[0_4px_24px_rgba(0,0,0,0.55)]',
            ]"
            :style="logoWrapStyle"
          >
            <img
              v-if="logoUrl"
              :src="logoUrl"
              :alt="logoAlt"
              class="player-idle-chrome-logo"
            >
          </div>
          <div
            class="flex min-h-0 w-full min-w-0 flex-col items-center justify-center transition-all duration-[950ms] motion-reduce:duration-75 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
            :class="compactLayout ? 'flex-1' : 'shrink-0 pt-[5vh]'"
          >
            <slot />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, toRefs } from 'vue'
import { useIdleScreensaverMotion } from '@/composables/useIdleScreensaverMotion'
import { usePlayerBranding } from '@/composables/usePlayerBranding'
import { usePlayerViewportRotationClasses } from '@/composables/usePlayerViewportRotationClasses'

const props = defineProps<{
  /** Logo smaller at top; main content area fills remaining height (hibernation clock). */
  compactLayout: boolean
  /** Full-screen blackout (hibernation timeout). */
  hibernate: boolean
  /** Bounce logo + slot together to reduce OLED/LCD burn-in on long idle. */
  screensaverMotion?: boolean
  /** Optional extra class on the logo wrapper (e.g. registration pairing sizing). */
  logoWrapClass?: string
  /** Optional inline bounds on the logo wrapper (orientation-specific registration layout). */
  logoWrapStyle?: Record<string, string>
}>()

const { compactLayout, screensaverMotion, logoWrapStyle } = toRefs(props)
const { viewportRotationClasses } = usePlayerViewportRotationClasses()
const { logoUrl, logoAlt, useDefaultChrome, backgroundLayerStyle } = usePlayerBranding()

const screensaverBoundsRef = ref<HTMLElement | null>(null)
const screensaverContentRef = ref<HTMLElement | null>(null)
const screensaverEnabled = computed(
  () => screensaverMotion.value === true && !compactLayout.value
)
useIdleScreensaverMotion(screensaverContentRef, screensaverBoundsRef, screensaverEnabled)
</script>
